import {
  Button,
  ConfirmDialog,
  EmptyState,
  FilterSegments,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  formatCurrency,
  plural,
  toast,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router'
import { ListFooter } from '@/app/list-footer'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { useDebounced } from '@/lib/use-debounced'
import { useStoredFilter } from '@/lib/use-stored-filter'
import { routes } from '@/routes'
import {
  useProcedureAction,
  useProcedureUsage,
  useProcedures,
  type Procedure,
  type ProcedureFilter,
} from './api'
import { compositionLabel, documentsLabel } from './labels'

/**
 * Procedimentos da clínica (docs/areas/administracao.md, "Procedimentos"; RN-ADM-36 e 38).
 * Arquétipo: lista de trabalho. Abre em "Sem vínculo" enquanto houver procedimento importado
 * sem item do catálogo; recepção e prestador leem, sem ações. Uma ação primária ("+ Adicionar
 * procedimento"); "Adicionar vários" e "Importar CSV" (RN-ADM-37) entram em contorno.
 */

const FILTERS: ProcedureFilter[] = ['pending', 'all', 'single', 'club_or_package', 'archived']

export function ProceduresPage() {
  const session = useSession()
  const navigate = useNavigate()
  const canManage = session.canManageClinic

  const stored = useStoredFilter('procedures', FILTERS, 'all')
  const { setFilter } = stored
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebounced(search.trim())
  // A tela abre no que tem pendência (RN-ADM-38), até a pessoa escolher um segmento: o número de
  // "sem vínculo" vem na primeira resposta, e a lista troca sozinha para ele.
  const [pendingSeen, setPendingSeen] = useState(0)
  const filter: ProcedureFilter = !stored.hasChosen && pendingSeen > 0 ? 'pending' : stored.filter
  const query = useProcedures({ filter, search: debouncedSearch || undefined, page })
  const counts = query.data?.meta.counts
  const pendingCount = counts?.pending ?? 0
  if (pendingCount !== pendingSeen) setPendingSeen(pendingCount)

  const [removing, setRemoving] = useState<Procedure | null>(null)
  const usage = useProcedureUsage(removing?.id ?? null)
  const action = useProcedureAction()

  const options: FilterSegmentOption<ProcedureFilter>[] = [
    ...(pendingCount > 0 || filter === 'pending'
      ? [{ value: 'pending' as const, label: 'Sem vínculo', count: pendingCount }]
      : []),
    { value: 'all', label: 'Todos', count: counts?.all },
    { value: 'single', label: 'Avulsos', count: counts?.single },
    { value: 'club_or_package', label: 'Clubes e pacotes', count: counts?.club_or_package },
    { value: 'archived', label: 'Arquivados', count: counts?.archived },
  ]

  function run(procedure: Procedure, kind: 'archive' | 'restore' | 'delete') {
    action.mutate(
      { id: procedure.id, action: kind },
      {
        onSuccess: () => {
          setRemoving(null)
          if (kind === 'archive') toast(`"${procedure.name}" arquivado — volta a qualquer momento`)
          if (kind === 'restore') toast(`"${procedure.name}" restaurado — volta ao catálogo`)
          if (kind === 'delete') toast(`"${procedure.name}" excluído do catálogo`)
        },
        onError: (error) => {
          setRemoving(null)
          toast(errorMessage(error))
        },
      },
    )
  }

  const procedures = query.data?.data ?? []
  const isEmptyCatalog =
    !query.isPending && (counts?.all ?? 0) === 0 && (counts?.archived ?? 0) === 0
  const showSearch = (counts?.all ?? 0) > 20 || search !== ''

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Administração' }, { label: 'Procedimentos' }]}
        actions={
          canManage ? (
            <>
              {/* No celular cabe uma ação no cabeçalho; "vários" e a importação continuam no estado vazio. */}
              <Button variant="outline" size="sm" className="max-desk:hidden" asChild>
                <Link to={routes.admin.importProcedures}>Importar CSV</Link>
              </Button>
              <Button variant="outline" size="sm" className="max-desk:hidden" asChild>
                <Link to={routes.admin.addProcedures}>Adicionar vários</Link>
              </Button>
              <Button size="sm" asChild>
                <Link to={routes.admin.newProcedure}>+ Adicionar procedimento</Link>
              </Button>
            </>
          ) : null
        }
      />
      <Page wide>
        <PageTitle
          title="Procedimentos"
          subtitle="O catálogo que alimenta contratos e planos: nome, valor e condição de pagamento."
        />

        {query.isError ? (
          <LoadError
            className="mt-6"
            title="Não conseguimos carregar os procedimentos"
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyCatalog ? (
          <EmptyState
            className="mt-6"
            title="Nenhum procedimento ainda"
            description="O catálogo é o que a clínica vende. Cada procedimento vem da lista da Westhetics e já traz os termos que o paciente assina; sem ele não há contrato nem plano de tratamento."
            action={
              canManage ? (
                <>
                  <Button asChild>
                    <Link to={routes.admin.addProcedures}>Escolher procedimentos</Link>
                  </Button>
                  <Button variant="outline" asChild>
                    <Link to={routes.admin.importProcedures}>Importar CSV</Link>
                  </Button>
                </>
              ) : undefined
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center justify-between gap-3">
              <FilterSegments
                label="Filtrar procedimentos"
                options={options}
                value={filter}
                onChange={(value) => {
                  setFilter(value)
                  setPage(1)
                }}
              />
              {showSearch ? (
                <SearchInput
                  className="tablet:max-w-[300px]"
                  value={search}
                  onChange={(value) => {
                    setSearch(value)
                    setPage(1)
                  }}
                  placeholder="Buscar por nome ou descrição"
                  aria-label="Buscar procedimento"
                />
              ) : null}
            </div>

            <div className="mt-4">
              {query.isPending ? (
                <TableSkeleton rows={6} columns={4} />
              ) : procedures.length === 0 ? (
                <EmptyState
                  title={
                    debouncedSearch
                      ? `Nenhum procedimento para "${debouncedSearch}"`
                      : filter === 'archived'
                        ? 'Nenhum procedimento arquivado'
                        : filter === 'pending'
                          ? 'Nenhum procedimento sem vínculo'
                          : 'Nenhum procedimento neste filtro'
                  }
                  description={
                    debouncedSearch
                      ? 'A busca cobre o nome, a descrição e o nome do procedimento na lista da Westhetics.'
                      : filter === 'archived'
                        ? 'O que você arquivar aparece aqui e volta a qualquer momento.'
                        : 'Todo procedimento desta clínica já está ligado à lista da Westhetics.'
                  }
                  action={
                    debouncedSearch ? (
                      <Button variant="outline" onClick={() => setSearch('')}>
                        Limpar busca
                      </Button>
                    ) : (
                      <Button variant="outline" onClick={() => setFilter('all')}>
                        Ver todos
                      </Button>
                    )
                  }
                />
              ) : (
                <Table>
                  <TableHeader>
                    <tr>
                      <TableHead>Procedimento</TableHead>
                      <TableHead>Documentos</TableHead>
                      <TableHead numeric>Valor</TableHead>
                      <TableHead>Pagamento</TableHead>
                      {canManage ? <TableHead aria-label="Ações" /> : null}
                    </tr>
                  </TableHeader>
                  <TableBody>
                    {procedures.map((procedure) => {
                      const composition = compositionLabel(procedure)
                      return (
                        <TableRow
                          key={procedure.id}
                          onClick={
                            canManage && !procedure.archivedAt
                              ? () => navigate(routes.admin.procedure(procedure.id))
                              : undefined
                          }
                        >
                          <TableCell primary>
                            {/* Um filho só: no celular a célula é flex, e o nome fica acima da composição. */}
                            <div className="min-w-0">
                              {procedure.name}
                              {composition ? (
                                <div className="font-body text-secondary mt-1 text-[12px] leading-[1.5] font-light">
                                  {composition}
                                </div>
                              ) : null}
                            </div>
                          </TableCell>
                          <TableCell label="Documentos" muted>
                            {procedure.isPendingCatalogLink ? (
                              <StatusTag level="attention">Sem vínculo com o catálogo</StatusTag>
                            ) : procedure.hasConsentForm ? (
                              documentsLabel(procedure.documents.length)
                            ) : (
                              <StatusTag level="attention">Sem termo</StatusTag>
                            )}
                          </TableCell>
                          <TableCell label="Valor" numeric>
                            {formatCurrency(procedure.amountCents / 100)}
                          </TableCell>
                          <TableCell label="Pagamento" muted>
                            {procedure.paymentTermsSummary || procedure.paymentTermsText || '—'}
                          </TableCell>
                          {canManage ? (
                            <TableCell>
                              <RowActions>
                                {procedure.archivedAt ? (
                                  <button type="button" onClick={() => run(procedure, 'restore')}>
                                    restaurar
                                  </button>
                                ) : (
                                  <>
                                    <Link to={routes.admin.procedure(procedure.id)}>
                                      {procedure.isPendingCatalogLink ? 'vincular' : 'editar'}
                                    </Link>
                                    <button type="button" onClick={() => setRemoving(procedure)}>
                                      excluir
                                    </button>
                                  </>
                                )}
                              </RowActions>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              )}
              {query.data && procedures.length > 0 ? (
                <ListFooter
                  meta={query.data.meta}
                  noun={['procedimento', 'procedimentos']}
                  note="em ordem alfabética"
                  onPage={setPage}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>

      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => {
          if (!open) setRemoving(null)
        }}
        title={`Excluir "${removing?.name ?? ''}"?`}
        effect={
          <>
            O procedimento sai do catálogo e não pode mais ser escolhido em novos documentos.{' '}
            {usageSentence(usage.data)}
          </>
        }
        alternative={{
          description:
            'Alternativa: arquivar. Some do catálogo, mas volta a qualquer momento e mantém o histórico completo.',
          label: 'Arquivar',
          onSelect: () => removing && run(removing, 'archive'),
        }}
        confirmLabel="Excluir"
        loading={action.isPending}
        loadingLabel="Excluindo…"
        onConfirm={() => removing && run(removing, 'delete')}
      />
    </>
  )
}

/** O efeito real da exclusão, com os números do uso (RN-ADM-36). */
function usageSentence(usage: { contractCount: number; treatmentPlanCount: number } | undefined) {
  if (!usage) return 'Nada assinado se desfaz.'
  if (usage.contractCount === 0 && usage.treatmentPlanCount === 0) {
    return 'Nenhum plano ou contrato usa este procedimento.'
  }
  return `Os ${plural(usage.treatmentPlanCount, 'plano')} e ${plural(usage.contractCount, 'contrato')} que já o usam continuam intactos — nada assinado se desfaz.`
}
